/**
 * computeCrossTabErrors — Pure cross-tab validation engine for SC V4.
 *
 * Takes a snapshot of all Zustand-cached tab data and returns a map of
 * { tabId: boolean } indicating which tabs have active errors.
 *
 * This is intentionally a pure function (no side effects, no hooks) so it
 * can be called from any React context AND is trivially unit-testable.
 *
 * HOW TO ADD A NEW RULE:
 *   1. Add the required data slice to CrossTabState
 *   2. Populate it in useSCDataPrefetch + the relevant Zustand store slice
 *   3. Add one `if` block here
 *   → Zero changes to any individual tab component.
 */

export interface CrossTabState {
  /** Standards selected on Tab 1 (SC Details) */
  scStandards: string[];

  /** Detail records for each facility, keyed by facilityId.
   *  Each record must include facilityInfo.facilityStandard: string[] */
  facilityDetailsCache: Record<string, any>;

  /** Claims currently saved to the API (Tab 7) */
  savedClaims: string[];

  /** Facilities whose standard warnings have been dismissed */
  reviewedFacilities: string[];
}

export function computeCrossTabErrors(state: CrossTabState): Record<string, boolean> {
  const errors: Record<string, boolean> = {};
  const { scStandards, facilityDetailsCache, savedClaims, reviewedFacilities } = state;

  // ── Rule 1 — facility-details (Tab 3) ────────────────────────────────────
  // Every facility must include ALL SC standards in its facilityStandard array.
  // Triggered when: user changes standards on Tab 1 after saving facilities.
  if (scStandards.length > 0 && Object.keys(facilityDetailsCache).length > 0) {
    // Filter out duplicate/alternative keys (like TE-ID keys) and keep only the canonical facilityId keys
    const canonicalDetails = Object.entries(facilityDetailsCache)
      .filter(([key, detail]) => {
        const facilityId = detail?.facilityInfo?.facilityId || detail?.facilityId || '';
        const facilityTeId = detail?.facilityInfo?.facilityTeId || detail?.facilityTeId || '';
        return key === facilityId || (!facilityId && key === facilityTeId);
      })
      .map(([_, detail]) => detail);

    const anyMismatch = canonicalDetails.some((detail: any) => {
      // 1. Robust ID Extraction (Root or Nested)
      const facilityId = detail?.facilityInfo?.facilityId || detail?.facilityInfo?.id || detail?.facilityId || detail?.id || '';
      const facilityTeId = detail?.facilityInfo?.facilityTeId || detail?.facilityTeId || '';
      const facilityType = detail?.facilityInfo?.facilityType || detail?.facilityType || '';

      // 2. Robust Standard Extraction (Root or Nested)
      // List API usually nests in facilityInfo; Detail API usually puts at root.
      const rawStd = detail?.facilityInfo?.facilityStandard ?? detail?.facilityStandard;
      
      // If we have NO standard information at all, the background prefetch is likely 
      // still loading or the record is invalid. Don't trigger a "missing" error yet.
      if (rawStd === undefined || rawStd === null) {
        return false;
      }

      let facilityStds: string[] = [];
      if (Array.isArray(rawStd)) {
        facilityStds = rawStd;
      } else if (typeof rawStd === 'string') {
        facilityStds = rawStd.split(',').map(s => s.trim()).filter(Boolean);
      } else {
        facilityStds = [String(rawStd)].filter(Boolean);
      }

      // 3. Review Status (Check both ID types)
      const isReviewed = 
        (facilityId && reviewedFacilities.includes(facilityId)) || 
        (facilityTeId && reviewedFacilities.includes(facilityTeId));

      const hasMissingStds = scStandards.some((std: string) => !facilityStds.includes(std));
      const hasExtraStds = facilityStds.some((std: string) => !scStandards.includes(std));
      
      // ✅ If standards match perfectly, there is nothing to review.
      if (!hasMissingStds && !hasExtraStds) {
        return false;
      }

      if (hasExtraStds) {
        // Extra standards are ALWAYS an error (must be removed)
        return true;
      }

      if (hasMissingStds) {
        // Only return true (error) if it's an MF (always mandatory) or it hasn't been reviewed
        if (facilityType === 'MF' || !isReviewed) {
          return true;
        }
      }
      return false;
    });
    errors['facility-details'] = anyMismatch;
  }

  // ── Rule 2 — component-details (Tab 7) ───────────────────────────────────
  // Every SC standard must have at least one saved claim whose code starts with that standard.
  // Triggered when: user changes standards on Tab 1 after saving claims.
  if (scStandards.length > 0) {
    const anyMissingClaim = scStandards.some(
      (std: string) => !savedClaims.some((claim: string) => claim.startsWith(std))
    );
    errors['component-details'] = anyMissingClaim;
  }

  return errors;
}
